import { type ReactNode } from 'react';
import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox';
import styles from './Checkbox.module.css';

type ICheckboxProps = {
  checked: boolean;
  onCheckedChange?: (next: boolean) => void;
  ariaLabel: string;
  /**
   * When true, the checkbox is rendered as a non-interactive visual mirror
   * of state — used inside a clickable parent (e.g. a row that is itself
   * the toggle button). The parent owns click + keyboard; the checkbox just
   * reflects `checked` so users can see the selected state.
   */
  presentational?: boolean;
  disabled?: boolean;
};

function CheckIcon(): ReactNode {
  return (
    <svg
      viewBox="0 0 12 12"
      width="12"
      height="12"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M2.5 6.2 L4.8 8.5 L9.5 3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Base UI renders TWO nodes per checkbox: the visible control, plus a
 * visually-hidden `<input type="checkbox" aria-hidden>` beside it that exists
 * only so forms and the browser see a genuine checkbox. That twin is why the
 * visible control's ELEMENT TYPE is load-bearing here, and why this component
 * renders it two different ways.
 *
 * INTERACTIVE (default) → a real `<button>`.
 * Call sites wrap the checkbox in a `<label>` so the text is clickable, and a
 * `<label>` activates the first *labelable* element inside it. Base UI's
 * default `<span role="checkbox">` is not labelable, so the label reached past
 * it to the hidden input: clicking the label text focused a node marked
 * `aria-hidden`, which Chrome blocks outright ("aria-hidden on a focused
 * element") and which strands screen-reader users on a node they were told to
 * ignore. A `<button>` IS labelable, so the label binds to the control the user
 * can actually see. `type="button"` stops it submitting the form it sits in.
 *
 * PRESENTATIONAL → stays a `<span>`, unfocusable and click-through.
 * These sit inside a row that is itself a `<button>`, so rendering a button
 * would nest one inside another — invalid HTML. `inert` + `pointer-events:
 * none` (CSS) block focus and clicks without `aria-hidden`, which would trip
 * that same focused-but-hidden warning as Base UI takes pointer focus on
 * mousedown.
 *
 * Knock-on worth knowing: a native `<button>` carries `disabled` natively, so a
 * disabled interactive checkbox leaves the tab order — the `<span>` variant
 * announced `aria-disabled` and stayed focusable.
 */
export function Checkbox({
  checked,
  onCheckedChange,
  ariaLabel,
  presentational = false,
  disabled = false,
}: ICheckboxProps) {
  return (
    <BaseCheckbox.Root
      checked={checked}
      onCheckedChange={presentational ? undefined : onCheckedChange}
      disabled={disabled}
      aria-label={ariaLabel || undefined}
      className={styles.root}
      data-presentational={presentational || undefined}
      // Labelable <button> when interactive, inert <span> when presentational —
      // see the note above; both halves are load-bearing.
      render={presentational ? undefined : <button type="button" />}
      nativeButton={!presentational}
      tabIndex={presentational ? -1 : undefined}
      // React 19 supports `inert` as a real boolean prop.
      inert={presentational || undefined}
    >
      <BaseCheckbox.Indicator className={styles.indicator}>
        <CheckIcon />
      </BaseCheckbox.Indicator>
    </BaseCheckbox.Root>
  );
}
